import { useState, useEffect, useRef } from 'react';
import { useConfig } from '@common/Hooks/useConfig';
import { prepareTrackingDataAttributes } from '@common/Listing/helpers/tracking';
import ProductCard from '@common/ProductCard/containers/ProductCardRedesign';
import { CARD_TYPES } from '@common/ProductCard/helpers/constants';
import { REAL_LIST_VALUES, trackProductImpressions } from '@common/Tracking';
import { fetchCarouselProducts } from '../helpers/fetchCarouselProducts';
import CarouselBanner from './CarouselBanner';

import '../styles/CarouselSection.scss';

function SkeletonItem() {
    return (
        <div className="loading-card product-mini-list__item carousel__product">
            <div className="loading-card__image animated-background" />
            <div className="loading-card__header animated-background" />
            <div className="loading-card__text animated-background" />
            <div className="loading-card__text animated-background" />
        </div>
    );
}

function CarouselSectionDesktop({
    hasImpressions,
    isSearchNoResult,
    data,
    navigationButtonsPosition,
    productCardClassName,
    productCardType,
    reallistValue,
    url,
    className
}) {
    const containerRef = useRef();
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const carouselProps = {
        products,
        itemsPerRow: 5
    };

    const config = useConfig();
    const hasConfigProperties = Object.keys(config).length > 0;

    const imageDimensions = hasConfigProperties ? Object.values(config.productCardImageDimensions)
        : App.Config.availableImageDimensions;

    const bigListingImageDimension = hasConfigProperties
        ? config.productCardImageDimensions.large
        : App.Config.imageDimensionListingBig;

    const skeletonMarkup = [];

    for (let i = 0; i < carouselProps.itemsPerRow; i++) {
        skeletonMarkup.push(<SkeletonItem key={i} />);
    }

    useEffect(() => {
        const handleProductsFetch = async () => {
            try {
                const response = await fetchCarouselProducts(url);
                setProducts(response.product_cards.slice(0, 15));
            } catch (error) {} finally {
                setIsLoading(false);
            }
        };

        if (isSearchNoResult) {
            setProducts(data);
            setIsLoading(false);
        } else {
            handleProductsFetch();
        }
    }, [data, isSearchNoResult, url]);

    useEffect(() => {
        if (hasImpressions && products.length > 0) {
            trackProductImpressions(containerRef.current);
        }
    }, [hasImpressions, products]);

    if (isLoading) {
        return (
            <div className="is-loading carousel">
                <div className="carousel__slide">
                    {[...skeletonMarkup]}
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={className}
        >
            <CarouselBanner
                group={REAL_LIST_VALUES.bestsellers}
                navigationButtonsPosition={navigationButtonsPosition}
            >
                {products.map((product, index) => (
                    <ProductCard
                        key={product.variants[0].ean_id}
                        hasToShowFavoriteButton
                        hasToTrackImpressions
                        availableImageDimensions={imageDimensions}
                        className={productCardClassName || 'product-card'}
                        imageSizes={{ dimension: bigListingImageDimension }}
                        product={product}
                        trackingValue={reallistValue || REAL_LIST_VALUES.frontPageCarousel}
                        trackingValues={{
                            bmp: {
                                itemIndex: index,
                                group: REAL_LIST_VALUES.bestsellers
                            },
                            dataAttributes: prepareTrackingDataAttributes({ product, index })
                        }}
                        type={productCardType || CARD_TYPES.frontPageCarousel}
                    />
                ))}
            </CarouselBanner>
        </div>
    );
}

export default CarouselSectionDesktop;
